import React, { useState } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { AnimatedPage } from '@theonefamily/ui';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';

const HubBar = React.lazy(() => import('hubBar/HubBar'));
import {
  LayoutDashboard, Users, UserSearch, Briefcase, Calendar, Star, GraduationCap,
  UserPlus, UserMinus, Network, Dna, DollarSign, BarChart3, FileSignature,
  Settings, ChevronLeft, ChevronRight, Menu, Shield, ScanSearch, Brain,
  Grid3X3, ClipboardList, TrendingDown, MessageSquare, Clock, GitBranch,
  FlaskConical, FileCheck, Globe, Crown, Activity, Wand2, PieChart,
  Heart, Zap,
} from 'lucide-react';

const NAV_ITEMS = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/theo-hr', icon: Brain, label: 'THEO HR' },
  { to: '/employees', icon: Users, label: 'Employees' },
  { to: '/candidates', icon: UserSearch, label: 'Candidates' },
  { to: '/jobs', icon: Briefcase, label: 'Job Postings' },
  { to: '/time-off', icon: Calendar, label: 'Time Off' },
  { to: '/reviews', icon: Star, label: 'Reviews' },
  { to: '/peer-feedback', icon: MessageSquare, label: 'Peer Feedback' },
  { to: '/training', icon: GraduationCap, label: 'Training' },
  { to: '/career-paths', icon: GitBranch, label: 'Career Paths' },
  { to: '/onboarding', icon: UserPlus, label: 'Onboarding' },
  { to: '/offboarding', icon: UserMinus, label: 'Offboarding' },
  { to: '/org-chart', icon: Network, label: 'Org Chart' },
  { to: '/succession-planning', icon: Crown, label: 'Succession' },
  { to: '/workdna', icon: Dna, label: 'WorkDNA' },
  { to: '/team-chemistry', icon: FlaskConical, label: 'Team Chemistry' },
  { to: '/compensation', icon: DollarSign, label: 'Compensation' },
  { to: '/compensation-intelligence', icon: PieChart, label: 'Comp Intel' },
  { to: '/benefits', icon: Heart, label: 'Benefits' },
  { to: '/policies', icon: Shield, label: 'Policies' },
  { to: '/hr/background-checks', icon: ScanSearch, label: 'Background Checks' },
  { to: '/i9-everify', icon: FileCheck, label: 'I-9 / E-Verify' },
  { to: '/global-workforce', icon: Globe, label: 'Global Workforce' },
  { to: '/skills-matrix', icon: Grid3X3, label: 'Skills Matrix' },
  { to: '/pulse-surveys', icon: ClipboardList, label: 'Pulse Surveys' },
  { to: '/engagement', icon: Activity, label: 'Engagement' },
  { to: '/attrition-model', icon: TrendingDown, label: 'Attrition Risk' },
  { to: '/shift-intelligence', icon: Clock, label: 'Shift Intel' },
  { to: '/candidate-sourcing', icon: Zap, label: 'Sourcing Intel' },
  { to: '/job-description-generator', icon: Wand2, label: 'JD Generator' },
  { to: '/reports', icon: BarChart3, label: 'Reports' },
  { to: '/esign', icon: FileSignature, label: 'E-Signature' },
  { to: '/admin', icon: Settings, label: 'Admin' },
];

export function Layout() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { isOnline } = useNetworkStatus();

  return (
    <>
      {!isOnline && (
        <div className="sticky top-0 z-50 bg-destructive text-destructive-foreground px-4 py-2 text-center text-sm font-medium">
          You&apos;re offline — changes will sync when reconnected
        </div>
      )}
      <React.Suspense fallback={<div style={{ height: 44 }} />}>
        <HubBar
          currentProduct="people"
          apiBase={import.meta.env.VITE_BUS_URL ?? 'https://api.theonebus.app'}
          hubUrl={import.meta.env.VITE_HUB_URL ?? 'https://hub.theonestack.com'}
          supportConfig={{
            apiBaseUrl: import.meta.env.VITE_OPS_CENTER_API_URL ?? 'https://api.theonestack.com',
            apiKey: import.meta.env.VITE_OPS_CENTER_API_KEY ?? '',
            platformId: 'people',
            platformName: 'The One People',
          }}
        />
      </React.Suspense>
    <div className="flex overflow-hidden" style={{ marginTop: '48px', height: 'calc(100vh - 48px)' }}>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 bg-black/60 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static z-50 h-full flex flex-col border-r border-border bg-surface-1
        transition-all duration-200
        ${collapsed ? 'w-16' : 'w-56'}
        ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Logo */}
        <div className="flex items-center gap-2 px-4 h-14 border-b border-border shrink-0">
          <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
            <Users className="w-4 h-4 text-amber-500" />
          </div>
          {!collapsed && <span className="font-semibold text-sm text-foreground">The One People</span>}
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-2 px-2">
          {NAV_ITEMS.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) => `
                flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors mb-0.5
                ${isActive
                  ? 'bg-amber-500/10 text-amber-500 font-medium'
                  : 'text-muted-foreground hover:text-foreground hover:bg-surface-3'
                }
              `}
            >
              <Icon className="w-4 h-4 shrink-0" />
              {!collapsed && <span>{label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Collapse toggle */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="hidden lg:flex items-center justify-center h-10 border-t border-border text-muted-foreground hover:text-foreground"
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header className="h-14 border-b border-border flex items-center px-4 gap-4 shrink-0 bg-surface-1">
          <button onClick={() => setMobileOpen(true)} className="lg:hidden text-muted-foreground hover:text-foreground">
            <Menu className="w-5 h-5" />
          </button>
          <h1 className="text-sm font-medium text-foreground">
            {NAV_ITEMS.find(n => n.to === location.pathname)?.label ?? 'The One People'}
          </h1>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-6">
          <AnimatedPage>
            <Outlet />
          </AnimatedPage>
        </main>
      </div>
    </div>
    </>
  );
}
